import React from 'react'
import _ from 'underscore'
import { BookOpen, Check, ChevronRight, Plus, TriangleAlert } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import CustomerPreferences from './customer_preferences'
import Address from '../../../address/show'
import store from '../../../../state/store'

const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground'

class LessonPlanLessonSetup extends React.Component {

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.lesson_plan !== this.props.lesson_plan)
      return true

    if (nextProps.enquiry_id !== this.props.enquiry_id)
      return true

    if (nextProps.subjects !== this.props.subjects)
      return true

    if (nextProps.learners !== this.props.learners)
      return true

    if (nextProps.google_map_ui !== this.props.google_map_ui)
      return true

    if (nextState !== this.state)
      return true

    return false
  }

  onSubjectClick = (subject) => {
    if (this.props.lesson_plan.enquiry_subject_ids.includes(subject.id))
      store.emit('enquiry:lesson_plans:destroy_subject', this.props.enquiry_id, this.props.lesson_plan.id, subject.id)
    else
      store.emit('enquiry:lesson_plans:create_subject', this.props.enquiry_id, this.props.lesson_plan.id, subject.id)
  }

  onLearnerClick = (learner) => {
    if (this.props.lesson_plan.enquiry_learner_ids.includes(learner.id))
      store.emit('enquiry:lesson_plans:destroy_learner', this.props.enquiry_id, this.props.lesson_plan.id, learner.id)
    else
      store.emit('enquiry:lesson_plans:create_learner', this.props.enquiry_id, this.props.lesson_plan.id, learner.id)
  }

  onAddressChange = (params = {}) => {
    store.emit('enquiry:address:update', this.props.lesson_plan.address.id, params)
  }

  getMarkers() {
    const address = this.props.lesson_plan.address
    let content = address.formatted_address

    if (!_.isEmpty(address.address_type))
      content += `<br/>(${address.address_type} address)`

    if (!_.isEmpty(address.note))
      content += `<br/>Note: ${address.note}`

    return [{
      latitude: address.latitude,
      longitude: address.longitude,
      content: content,
      open: true
    }]
  }

  renderLearnerDescription = (learner) => {
    if (learner.gender && learner.learner_level)
      return (
        <p className="text-xs font-semibold capitalize text-muted-foreground">
          <span>{learner.gender}</span>
          <span>,&nbsp;{learner.learner_level}</span>
        </p>
      )
    else if (learner.gender && !learner.learner_level)
      return (
        <p className="text-xs font-semibold capitalize text-muted-foreground">
          <span>{learner.gender}</span>
        </p>
      )
    else if (learner.learner_level && !learner.gender)
      return (
        <p className="text-xs font-semibold capitalize text-muted-foreground">
          <span>{learner.learner_level}</span>
        </p>
      )

    return null
  }

  render() {
    const markers = this.getMarkers()

    return (
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="size-4" />
              Lesson Details
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            <div className="pb-4">
              <span className={SECTION_LABEL}>Subjects</span>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {_.map(this.props.subjects, (subject) => {
                  const selected = this.props.lesson_plan.enquiry_subject_ids.includes(subject.id)
                  const known = (subject.subject_category_name === this.props.lesson_plan.subject_category_name) && (subject.subject_level_name === this.props.lesson_plan.subject_level_name)

                  return (
                    <span key={subject.id} className={`inline-flex cursor-pointer items-center rounded-full border border-border py-1 pl-3 pr-2.5 text-[13px] ${selected ? 'border-foreground bg-foreground text-background' : 'bg-card hover:bg-muted/50'}`} onClick={() => this.onSubjectClick(subject)}>
                      {(!known) && <span title="Unknown subject"><TriangleAlert className="mr-1 size-3 text-warning"/></span>}
                      {subject.name} {subject.subject_level_name}
                      {subject.syllabus && <span>&nbsp;({subject.syllabus})</span>}
                      {selected ?
                        <Check className="ml-2 size-3.5"/>
                      :
                        <Plus className="ml-2 size-3.5 text-muted-foreground"/>
                      }
                    </span>
                  )
                })}
              </div>
            </div>

            <div className="py-4">
              <span className={SECTION_LABEL}>Learners</span>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {_.map(this.props.learners, (learner) => {
                  const selected = this.props.lesson_plan.enquiry_learner_ids.includes(learner.id)

                  return (
                    <span key={learner.id} className={`inline-flex cursor-pointer items-center rounded-full border border-border py-[3px] pl-1 pr-2.5 text-[13px] ${selected ? 'border-foreground bg-foreground text-background' : 'bg-card hover:bg-muted/50'}`} onClick={() => this.onLearnerClick(learner)}>
                      <img src={learner.profile_picture_url} alt="" className="mr-1.5 size-6 rounded-full"/>
                      {learner.name}
                      {selected ?
                        <Check className="ml-2 size-3.5"/>
                      :
                        <Plus className="ml-2 size-3.5 text-muted-foreground"/>
                      }
                    </span>
                  )
                })}
              </div>
            </div>

            <div className="py-4 last:pb-0">
              <Address
                key={this.props.lesson_plan.id}
                address={this.props.lesson_plan.address}
                loaded={this.props.google_map_ui.loaded}
                markers={markers}
                formatted_address={this.props.lesson_plan.address.formatted_address}
                flat_unit_estate={this.props.lesson_plan.address.flat_unit_estate}
                address_type={this.props.lesson_plan.address.address_type}
                address_note={this.props.lesson_plan.address.note}
                onChange={this.onAddressChange}
                store={store}
                embedded
              />
            </div>

            {this.props.lesson_plan.additional_notes &&
              <div className="pt-4">
                <span className={SECTION_LABEL}>Additional Notes</span>
                <p className="mt-1 text-sm"><ChevronRight className="mr-1 inline size-3.5"/>{this.props.lesson_plan.additional_notes}</p>
              </div>
            }
          </CardContent>
        </Card>
        <CustomerPreferences lesson_plan={this.props.lesson_plan} enquiry_id={this.props.enquiry_id}/>
      </div>
    )
  }
}

export default LessonPlanLessonSetup
